import { MonitorPlay, Play, Video } from 'lucide-react'
import { MriButton, cn } from '@mriqbox/ui-kit'
import { Avatar } from '@/components/Avatar'
import type { StageStream } from '@/components/StreamTile'

export interface AvailableStreamsProps {
    streams: StageStream[]
    /** Latest preview image per stream id. */
    thumbnails?: Record<string, string>
    onWatch: (id: string) => void
    onWatchAll: () => void
}

function actionLabel(entry: StageStream): string {
    if (entry.isSelf) return 'Mostrar prévia'
    return entry.kind === 'camera' ? 'Assistir câmera' : 'Assistir transmissão'
}

function subtitle(entry: StageStream): string {
    if (entry.isSelf) return entry.kind === 'camera' ? 'Sua câmera, prévia oculta' : 'Sua tela, prévia oculta'
    return entry.kind === 'camera' ? 'Câmera ligada' : 'Transmitindo a tela'
}

function Preview({ entry, thumbnail, avatarSize }: { entry: StageStream; thumbnail?: string; avatarSize: number }) {
    return thumbnail ? (
        <img
            src={thumbnail}
            alt={`Prévia de ${entry.nickname}`}
            draggable={false}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
        />
    ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-card to-background">
            <Avatar nickname={entry.nickname} avatar={entry.avatar} size={avatarSize} />
        </div>
    )
}

function LiveBadge({ entry }: { entry: StageStream }) {
    return (
        <span className={cn(
            'absolute left-2 top-2 flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white',
            entry.kind === 'camera' ? 'bg-primary/90' : 'bg-destructive'
        )}>
            {entry.kind === 'camera'
                ? <Video className="h-3 w-3" aria-hidden />
                : <MonitorPlay className="h-3 w-3" aria-hidden />}
            {entry.kind === 'camera' ? 'Câmera' : 'Ao vivo'}
        </span>
    )
}

/** Nothing on stage yet: a large preview with one obvious action, and nothing downloaded until the click. */
export function AvailableCards({ streams, thumbnails = {}, onWatch, onWatchAll }: AvailableStreamsProps) {
    const others = streams.filter((entry) => !entry.isSelf)
    return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-3 overflow-y-auto p-1">
            <ul className={cn(
                'grid w-full gap-3',
                streams.length === 1 ? 'max-w-3xl grid-cols-1' : 'max-w-5xl grid-cols-1 md:grid-cols-2'
            )}>
                {streams.map((entry) => (
                    <li key={entry.id}>
                        <div
                            role="presentation"
                            onClick={() => onWatch(entry.id)}
                            className="group relative aspect-video w-full cursor-pointer overflow-hidden rounded-xl border border-border/70 bg-card transition-colors hover:border-primary/60"
                        >
                            <Preview entry={entry} thumbnail={thumbnails[entry.id]} avatarSize={72} />
                            <div className="absolute inset-0 bg-black/45 transition-colors group-hover:bg-black/35" />
                            <LiveBadge entry={entry} />
                            <div className="absolute inset-0 flex items-center justify-center p-3">
                                <MriButton
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        onWatch(entry.id)
                                    }}
                                    className="h-11 px-5 text-sm font-bold shadow-lg shadow-black/50"
                                >
                                    <Play className="mr-2 h-4 w-4 fill-current" aria-hidden />
                                    {actionLabel(entry)}
                                </MriButton>
                            </div>
                            <div className="absolute inset-x-0 bottom-0 flex min-w-0 items-center gap-2 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8">
                                <Avatar nickname={entry.nickname} avatar={entry.avatar} size={28} />
                                <div className="min-w-0 text-left">
                                    <p className="truncate text-sm font-semibold text-white">
                                        {entry.isSelf ? 'Você' : entry.nickname}
                                    </p>
                                    <p className="truncate text-xs text-white/70">{subtitle(entry)}</p>
                                </div>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
            {others.length > 1 && (
                <MriButton variant="secondary" size="sm" onClick={onWatchAll}>
                    Assistir todas
                </MriButton>
            )}
            <p className="text-center text-xs text-muted-foreground">
                A imagem só é baixada quando você clica em assistir.
            </p>
        </div>
    )
}

/** With something on stage, the rest stays visible as small live previews above it. */
export function AvailableStrip({ streams, thumbnails = {}, onWatch, onWatchAll }: AvailableStreamsProps) {
    if (streams.length === 0) return null
    const others = streams.filter((entry) => !entry.isSelf)
    return (
        <div className="mb-2 flex shrink-0 items-center gap-2 overflow-x-auto pb-1">
            {streams.map((entry) => (
                <button
                    key={entry.id}
                    type="button"
                    onClick={() => onWatch(entry.id)}
                    title={actionLabel(entry)}
                    className="group relative aspect-video w-36 shrink-0 overflow-hidden rounded-lg border border-border/70 bg-card text-left transition-colors hover:border-primary/60 sm:w-44"
                >
                    <Preview entry={entry} thumbnail={thumbnails[entry.id]} avatarSize={36} />
                    <div className="absolute inset-0 bg-black/30 transition-colors group-hover:bg-black/10" />
                    <LiveBadge entry={entry} />
                    <span className="absolute inset-0 flex items-center justify-center">
                        <span className="flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-bold text-primary-foreground shadow-lg shadow-black/50">
                            <Play className="h-3 w-3 fill-current" aria-hidden />
                            {entry.isSelf ? 'Mostrar' : 'Assistir'}
                        </span>
                    </span>
                    <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/80 to-transparent px-2 pb-1 pt-4 text-[11px] font-semibold text-white">
                        {entry.isSelf ? 'Você' : entry.nickname}
                    </span>
                </button>
            ))}
            {others.length > 1 && (
                <button
                    type="button"
                    onClick={onWatchAll}
                    className="shrink-0 rounded-full px-2 py-1 text-xs font-medium text-primary hover:underline"
                >
                    Assistir todas
                </button>
            )}
        </div>
    )
}
